import type { ReactNode } from "react";
import { AlertCircle, CheckCircle2, Info, Loader2 } from "lucide-react";

// =============================================================================
// Shared presentation pieces.
//
// Small and unopinionated on purpose: the styling lives in index.css as
// component classes, so a visual change happens in one place rather than
// across a dozen pages, and a page that needs something bespoke can still
// write Tailwind directly instead of fighting a prop API.
// =============================================================================

export function PageHeader({
  title,
  subtitle,
  icon,
  actions,
}: {
  title: string;
  subtitle?: string;
  icon?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="mb-6 flex flex-wrap items-start justify-between gap-4">
      <div className="flex min-w-0 items-start gap-3">
        {icon ? (
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary-50 text-primary-700">
            {icon}
          </span>
        ) : null}
        {/* min-w-0 so a long title wraps instead of widening the header:
            a flex item defaults to min-width:auto and will not shrink below
            its content, which is how one long brand name pushed whole pages
            sideways on a phone. */}
        <div className="min-w-0">
          <h1 className="break-words text-xl font-semibold tracking-tight text-ink">{title}</h1>
          {subtitle ? <p className="mt-1 max-w-2xl text-sm text-muted">{subtitle}</p> : null}
        </div>
      </div>
      {/* Wraps, because on a phone the actions are usually a filter plus a
          button and they do not fit beside each other. Without this they
          simply overflowed the viewport. */}
      {actions ? (
        <div className="flex min-w-0 max-w-full flex-wrap items-center gap-2">{actions}</div>
      ) : null}
    </header>
  );
}

export function Card({
  children,
  className = "",
  title,
  description,
  actions,
}: {
  children?: ReactNode;
  className?: string;
  title?: string;
  description?: string;
  actions?: ReactNode;
}) {
  return (
    <section className={`card ${className}`}>
      {title ? (
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border px-5 py-4">
          <div>
            <h2 className="text-sm font-semibold text-ink">{title}</h2>
            {description ? <p className="mt-0.5 text-xs text-muted">{description}</p> : null}
          </div>
          {actions}
        </div>
      ) : null}
      <div className="p-5">{children}</div>
    </section>
  );
}

export function EmptyState({
  icon,
  title,
  description,
  action,
}: {
  icon?: ReactNode;
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 px-6 py-12 text-center">
      {icon ? (
        <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-primary-50 text-primary-600">
          {icon}
        </span>
      ) : null}
      <div>
        <p className="text-sm font-medium text-ink">{title}</p>
        {description ? (
          <p className="mx-auto mt-1 max-w-sm text-sm text-muted">{description}</p>
        ) : null}
      </div>
      {action}
    </div>
  );
}

type ToneName = "info" | "success" | "warning" | "danger";

const TONES: Record<ToneName, { box: string; icon: ReactNode }> = {
  info: {
    box: "border-primary-200 bg-primary-50 text-primary-900",
    icon: <Info size={15} className="shrink-0 text-primary-600" />,
  },
  success: {
    box: "border-success/25 bg-success/10 text-success",
    icon: <CheckCircle2 size={15} className="shrink-0" />,
  },
  warning: {
    box: "border-warning/25 bg-warning/10 text-warning",
    icon: <AlertCircle size={15} className="shrink-0" />,
  },
  danger: {
    box: "border-danger/25 bg-danger/10 text-danger",
    icon: <AlertCircle size={15} className="shrink-0" />,
  },
};

export function Notice({
  tone = "info",
  children,
}: {
  tone?: ToneName;
  children: ReactNode;
}) {
  const { box, icon } = TONES[tone];
  return (
    <div
      className={`flex items-start gap-2.5 rounded-xl border px-3.5 py-2.5 text-sm ${box}`}
      // Failures and confirmations both need announcing; "polite" so it waits
      // for a pause rather than interrupting what is being read.
      role="status"
      aria-live="polite"
    >
      {icon}
      <span className="leading-relaxed">{children}</span>
    </div>
  );
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: ToneName | "neutral";
}) {
  const styles: Record<string, string> = {
    neutral: "bg-border/60 text-muted",
    info: "bg-primary-100 text-primary-800",
    success: "bg-success/12 text-success",
    warning: "bg-warning/12 text-warning",
    danger: "bg-danger/12 text-danger",
  };
  return <span className={`badge ${styles[tone]}`}>{children}</span>;
}

export function Spinner({ className = "" }: { className?: string }) {
  return <Loader2 size={15} className={`animate-spin ${className}`} aria-hidden />;
}

export function SkeletonRows({ rows = 3 }: { rows?: number }) {
  return (
    <div className="space-y-2.5" aria-hidden>
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-11" />
      ))}
    </div>
  );
}
